import { useEffect, useMemo, useRef } from 'react';
import * as Y from 'yjs';
import { createClient } from '@/utils/supabase/component';
import { useWorkflowStore } from './store';
import { useUser } from '@/utils/useUser';
import { SupabaseProvider } from './yjs-supabase-provider';
import { Node, Edge } from '@xyflow/react';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Hook for Yjs-based real-time collaboration on workflows.
 * Uses CRDTs for conflict-free merging of concurrent edits.
 *
 * Benefits over previous implementation:
 * - Only sends deltas (changes), not entire state
 * - Automatic conflict resolution via CRDTs
 * - Scales to thousands of concurrent users
 * - Built-in awareness for presence tracking
 */

const LOCAL_ORIGIN = 'local-zustand-update';

export function useYjsCollaboration() {
  const isBrowser = typeof window !== 'undefined';
  const supabase = useMemo(
    () => (isBrowser ? createClient() : null),
    [isBrowser],
  );

  const workflowId = useWorkflowStore((state) => state.workflowId);
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const setNodes = useWorkflowStore((state) => state.setNodes);
  const setEdges = useWorkflowStore((state) => state.setEdges);
  const { user, userDetails } = useUser();

  // Refs to maintain state across renders
  const yjsDocRef = useRef<Y.Doc | null>(null);
  const providerRef = useRef<SupabaseProvider | null>(null);
  const isApplyingRemoteUpdateRef = useRef(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastSyncedStateRef = useRef<{ nodes: Node[]; edges: Edge[] } | null>(
    null,
  );
  const initialSeedTimeoutRef = useRef<number | null>(null);

  // Helpers
  const dedupeById = <T extends { id: string }>(items: T[]): T[] => {
    const map = new Map<string, T>();
    for (const item of items) {
      map.set(item.id, item);
    }
    return Array.from(map.values());
  };
  const colorFromId = (id: string | undefined) => {
    if (!id) return '#3b82f6';
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 85%, 55%)`;
  };

  // Initialize Yjs document and provider
  useEffect(() => {
    if (!workflowId || !user || !supabase) {
      return;
    }

    console.log('[Yjs Hook] ==== INITIALIZING YJS ====');

    // Create Yjs document
    const yjsDoc = new Y.Doc();
    yjsDocRef.current = yjsDoc;

    // Add unique ID for debugging
    (yjsDoc as any).__debugId = Math.random().toString(36).substring(7);
    console.log('[Yjs Hook] Created Y.Doc with ID:', (yjsDoc as any).__debugId);

    // Get shared types for nodes and edges (arrays)
    const yNodes = yjsDoc.getArray<Node>('nodes');
    const yEdges = yjsDoc.getArray<Edge>('edges');

    // Create Supabase Realtime channel with self-broadcast disabled
    const channel = supabase.channel(`workflow:${workflowId}`, {
      config: {
        presence: { key: user.id },
        broadcast: { self: false }, // Don't receive our own broadcasts
      },
    });

    // Subscribe to channel
    channel.subscribe(async (status) => {
      console.log('[Yjs Hook] Channel status:', status);

      if (status === 'SUBSCRIBED') {
        console.log('[Yjs Hook] Channel subscribed successfully');

        // Track user presence
        await channel.track({
          userId: user.id,
          name:
            userDetails?.first_name ||
            userDetails?.last_name ||
            user?.email ||
            'Guest',
          joinedAt: Date.now(),
        });

        // Initialize provider after channel is ready
        console.log('[Yjs Hook] Creating provider');
        const provider = new SupabaseProvider(yjsDoc, channel, workflowId);
        providerRef.current = provider;

        // Give other peers a brief window to respond with sync; if none, seed local state once
        // This prevents double-seeding when two clients join at the same time
        if (typeof window !== 'undefined') {
          initialSeedTimeoutRef.current = window.setTimeout(() => {
            const hasSynced = providerRef.current?.isSynced();
            const yNodesNow = yjsDoc.getArray<Node>('nodes');
            const yEdgesNow = yjsDoc.getArray<Edge>('edges');
            if (
              !hasSynced &&
              yNodesNow.length === 0 &&
              yEdgesNow.length === 0
            ) {
              yjsDoc.transact(() => {
                if (nodes.length > 0) {
                  yNodesNow.push(nodes);
                }
                if (edges.length > 0) {
                  yEdgesNow.push(edges);
                }
              }, LOCAL_ORIGIN);
              lastSyncedStateRef.current = {
                nodes: [...nodes],
                edges: [...edges],
              };
              console.log(
                '[Yjs Hook] Seeded initial state into empty Y.Doc after timeout',
              );
            }
          }, 400);
        }

        // Set awareness state
        provider.setAwarenessState({
          user: {
            id: user.id,
            name:
              userDetails?.first_name ||
              userDetails?.last_name ||
              user?.email ||
              'Guest',
            email: user?.email,
            color: colorFromId(user.id || user?.email || 'guest'),
          },
        });

        console.log('[Yjs Hook] Provider initialized');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('[Yjs Hook] Channel error');
      }
    });

    channelRef.current = channel;

    // Listen to Yjs changes and update Zustand store
    const handleYjsUpdate = (update: Uint8Array, origin: any) => {
      console.log(
        '[Yjs Hook] ✅ Update received on Y.Doc:',
        (yjsDoc as any).__debugId,
        'origin:',
        origin,
      );

      // Skip if this update came from local Zustand changes
      if (origin === LOCAL_ORIGIN) {
        console.log('[Yjs Hook] Skipping - local origin');
        return;
      }

      const yNodes = yjsDoc.getArray<Node>('nodes');
      const yEdges = yjsDoc.getArray<Edge>('edges');

      const rawNodes = yNodes.toArray();
      const rawEdges = yEdges.toArray();
      const newNodes = dedupeById<Node>(rawNodes);
      const newEdges = dedupeById<Edge>(rawEdges);

      // Guard: ignore empty updates if we previously had content (likely partial/out-of-order)
      const hadContentBefore =
        !!lastSyncedStateRef.current &&
        ((lastSyncedStateRef.current.nodes?.length || 0) > 0 ||
          (lastSyncedStateRef.current.edges?.length || 0) > 0);
      const nowEmpty = newNodes.length === 0 && newEdges.length === 0;
      const nodesEmptied =
        hadContentBefore &&
        newNodes.length === 0 &&
        (lastSyncedStateRef.current?.nodes?.length || 0) > 0;
      const edgesEmptied =
        hadContentBefore &&
        newEdges.length === 0 &&
        (lastSyncedStateRef.current?.edges?.length || 0) > 0 &&
        // only treat as suspicious if nodes still present (to avoid legit full clears)
        newNodes.length > 0;
      if (hadContentBefore && (nowEmpty || nodesEmptied || edgesEmptied)) {
        console.warn(
          '[Yjs Hook] Ignoring empty remote update; requesting resync',
        );
        try {
          providerRef.current?.requestSyncFromPeers?.();
        } catch (e) {
          console.warn('[Yjs Hook] Failed to request sync', e);
        }
        return;
      }

      console.log('[Yjs Hook] Applying remote update to Zustand:', {
        nodeCount: newNodes.length,
        edgeCount: newEdges.length,
      });

      // Update store without triggering our sync effect
      isApplyingRemoteUpdateRef.current = true;
      setNodes(newNodes);
      setEdges(newEdges);
      lastSyncedStateRef.current = { nodes: newNodes, edges: newEdges };

      // Use setTimeout to ensure state updates have propagated
      setTimeout(() => {
        isApplyingRemoteUpdateRef.current = false;
      }, 0);
    };

    yjsDoc.on('update', handleYjsUpdate);
    console.log(
      '[Yjs Hook] ✅ Attached update listener to Y.Doc:',
      (yjsDoc as any).__debugId,
    );

    // Cleanup
    return () => {
      console.log(
        '[Yjs Hook] ==== CLEANING UP YJS ====',
        (yjsDoc as any).__debugId,
      );
      yjsDoc.off('update', handleYjsUpdate);
      providerRef.current?.destroy();
      channel.unsubscribe();
      yjsDocRef.current = null;
      providerRef.current = null;
      channelRef.current = null;
      if (initialSeedTimeoutRef.current) {
        clearTimeout(initialSeedTimeoutRef.current);
        initialSeedTimeoutRef.current = null;
      }
    };
  }, [
    supabase,
    workflowId,
    user?.id,
    user?.email,
    userDetails?.first_name,
    userDetails?.last_name,
    setNodes,
    setEdges,
    // Note: nodes and edges are intentionally NOT in deps
    // to avoid recreation on every state change
  ]);

  // Sync Zustand changes to Yjs (debounced to batch rapid changes)
  useEffect(() => {
    if (
      !yjsDocRef.current ||
      !workflowId ||
      isApplyingRemoteUpdateRef.current
    ) {
      console.log('[Yjs Sync] Skipping sync - guard check failed:', {
        hasDoc: !!yjsDocRef.current,
        hasWorkflowId: !!workflowId,
        isApplying: isApplyingRemoteUpdateRef.current,
      });
      return;
    }

    // Debounce to batch rapid changes
    const timeoutId = setTimeout(() => {
      // Skip if already applying remote update
      if (isApplyingRemoteUpdateRef.current) {
        console.log('[Yjs Sync] Skipping sync - applying remote update');
        return;
      }

      const yNodes = yjsDocRef.current!.getArray<Node>('nodes');
      const yEdges = yjsDocRef.current!.getArray<Edge>('edges');

      // Check if state actually changed since last sync
      const lastState = lastSyncedStateRef.current;
      const nodesChanged =
        !lastState || JSON.stringify(lastState.nodes) !== JSON.stringify(nodes);
      const edgesChanged =
        !lastState || JSON.stringify(lastState.edges) !== JSON.stringify(edges);

      if (nodesChanged || edgesChanged) {
        console.log(
          '[Yjs Sync] Syncing local changes to Yjs Doc:',
          (yjsDocRef.current as any).__debugId,
          {
            nodesChanged,
            edgesChanged,
            nodeCount: nodes.length,
            edgeCount: edges.length,
          },
        );

        // Use Yjs transaction with origin to prevent echo
        // Sync nodes and edges atomically to avoid edge/node reference issues
        yjsDocRef.current!.transact(() => {
          if (nodesChanged) {
            yNodes.delete(0, yNodes.length);
            yNodes.push(nodes);
          }
          if (edgesChanged) {
            yEdges.delete(0, yEdges.length);
            yEdges.push(edges);
          }
        }, LOCAL_ORIGIN);

        lastSyncedStateRef.current = { nodes: [...nodes], edges: [...edges] };
      }
    }, 100); // 100ms debounce

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, workflowId]);

  // Return awareness info for presence UI
  return {
    provider: providerRef.current,
    awareness: providerRef.current?.getAwareness(),
  };
}
